import { ApiResponse } from 'src/app/models/api-response.interface';

export const RESIDENTIAL_CLIENT_TYPE = 0;
export const GLOBAL_HABITATIONAL_REPORT_TITLE = 'Reporte Global Habitacional';
export const GLOBAL_HABITATIONAL_CSV_PREFIX = 'global-habitacional';
export const HOUSING_TANKS_CSV_PREFIX = 'habitacional-tanques';

export type ReportColumnSummaryFormat = 'number' | 'currency' | 'percentage';
export type ReportColumnSummaryKind = 'total' | 'average';

export interface ReportColumnSummary {
  value: number;
  format: ReportColumnSummaryFormat;
  kind: ReportColumnSummaryKind;
}

export type ReportColumnSummaries = Record<string, ReportColumnSummary>;

export interface ReportColumnSummaryHeader {
  columnKey: string;
  headerName: string;
  format: ReportColumnSummaryFormat;
  kind: ReportColumnSummaryKind;
}

export interface HabitationalReportResponse<T> extends ApiResponse<T> {
  columnSummaries: ReportColumnSummaries;
}

export interface CommercialReportResponse<T> extends ApiResponse<T> {
  columnSummaries: ReportColumnSummaries;
}

export const GLOBAL_HABITATIONAL_SUMMARY_HEADERS: readonly ReportColumnSummaryHeader[] = [
  {
    columnKey: 'consumption_liters',
    headerName: 'Report-Total-Consumption-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'period_consumption_amount',
    headerName: 'Report-Total-Period-Consumption-Amount',
    format: 'currency',
    kind: 'total'
  },
  {
    columnKey: 'liter_price',
    headerName: 'Report-Average-Liter-Price',
    format: 'currency',
    kind: 'average'
  }
];

export const HOUSING_TANKS_SUMMARY_HEADERS: readonly ReportColumnSummaryHeader[] = [
  {
    columnKey: 'consumption_liters',
    headerName: 'Report-Total-Consumption-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'initial_tank_inventory_liters',
    headerName: 'Report-Total-Initial-Tank-Inventory-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'final_tank_inventory_liters',
    headerName: 'Report-Total-Final-Tank-Inventory-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'total_purchased_liters',
    headerName: 'Report-Total-Purchased-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'worked_liters',
    headerName: 'Report-Total-Worked-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'efficiency_liters',
    headerName: 'Report-Total-Efficiency-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'efficiency_percentage',
    headerName: 'Report-Total-Efficiency-Percentage',
    format: 'percentage',
    kind: 'total'
  },
  {
    columnKey: 'period_consumption_amount',
    headerName: 'Report-Total-Period-Consumption-Amount',
    format: 'currency',
    kind: 'total'
  },
  {
    columnKey: 'liter_price',
    headerName: 'Report-Average-Liter-Price',
    format: 'currency',
    kind: 'average'
  }
];

export const COMMERCIAL_REPORT_SUMMARY_HEADERS: readonly ReportColumnSummaryHeader[] = [
  {
    columnKey: 'consumption_liters',
    headerName: 'Report-Total-Consumption-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'initial_tank_inventory_liters',
    headerName: 'Report-Total-Initial-Tank-Inventory-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'final_tank_inventory_liters',
    headerName: 'Report-Total-Final-Tank-Inventory-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'total_purchased_liters',
    headerName: 'Report-Total-Purchased-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'worked_liters',
    headerName: 'Report-Total-Worked-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'efficiency_liters',
    headerName: 'Report-Total-Efficiency-Liters',
    format: 'number',
    kind: 'total'
  },
  {
    columnKey: 'efficiency_percentage',
    headerName: 'Report-Total-Efficiency-Percentage',
    format: 'percentage',
    kind: 'total'
  },
  {
    columnKey: 'period_consumption_amount',
    headerName: 'Report-Total-Period-Consumption-Amount',
    format: 'currency',
    kind: 'total'
  },
  {
    columnKey: 'admin_fee_amount',
    headerName: 'Report-Total-Admin-Fee-Amount',
    format: 'currency',
    kind: 'total'
  },
  {
    columnKey: 'liter_price',
    headerName: 'Report-Average-Liter-Price',
    format: 'currency',
    kind: 'average'
  }
];

export function hasReportValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

export function formatNfcStatus(value: string | null | undefined): string {
  const normalizedValue = value?.trim().toLowerCase();

  if (normalizedValue === 'sin nfc') {
    return 'No';
  }

  if (normalizedValue === 'con nfc') {
    return 'Sí';
  }

  return value ?? '';
}

export function formatAnovaStatus(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number') {
    return String(value);
  }

  return value.trim().toLowerCase() === 'sin anova' ? 'No' : value;
}

export function formatLocalDateForFilename(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function extractReportColumnSummaries(
  getHeader: (headerName: string) => string | null,
  definitions: readonly ReportColumnSummaryHeader[]
): ReportColumnSummaries {
  return definitions.reduce<ReportColumnSummaries>((summaries, definition) => {
    const rawValue = getHeader(definition.headerName);

    if (rawValue === null || rawValue.trim() === '') {
      return summaries;
    }

    const normalizedValue = rawValue
      .trim()
      .replace(/,/g, '')
      .replace(/[$%]/g, '');
    const value = Number(normalizedValue);

    if (!Number.isFinite(value)) {
      return summaries;
    }

    summaries[definition.columnKey] = {
      value,
      format: definition.format,
      kind: definition.kind
    };

    return summaries;
  }, {});
}
